<!--
  site-header.vue
  
  2025-06-04: Refactored to use external ShoppingCartModal component
  2025-06-04: Added shopping cart functionality with modal, cart management, and quote sending
-->
<template>
  <div v-if="!isAgentSite" class="site-header">
    <v-navigation-drawer
      v-if="isMobile"
      v-model="drawer"
      app
      hide-overlay
      right
      :color="primaryColor"
      dark
      width="100%"
      class="mobile-nav"
    >
      <div class="d-flex justify-end pa-4">
        <v-btn icon @click="drawer = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-list class="text-center pa-0 pt-8">
        <v-list-item to="/" class="mb-6" @click="drawer = false">
          <v-list-item-content>
            <v-list-item-title class="text-h5 font-weight-medium"
              >HOME</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>

        <v-list-item @click="listingsSearch" class="mb-6">
          <v-list-item-content>
            <v-list-item-title class="text-h5 font-weight-medium"
              >SEARCH</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="settings?.pages?.about?.active"
          to="/about"
          class="mb-6"
          @click="drawer = false"
        >
          <v-list-item-content>
            <v-list-item-title class="text-h5 font-weight-medium"
              >ABOUT US</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="settings?.pages?.contact?.active"
          to="/contact"
          class="mb-6"
          @click="drawer = false"
        >
          <v-list-item-content>
            <v-list-item-title class="text-h5 font-weight-medium"
              >CONTACT</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="settings?.business?.phone"
          :href="`tel:${settings.business.phone}`"
          class="mb-6"
        >
          <v-list-item-content>
            <v-list-item-title class="text-h5 font-weight-medium"
              >CALL US</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="settings?.show_whatsapp_link && settings?.business?.phone"
          :href="`https://wa.me/${settings.business.phone.replace(/\D/g, '')}`"
          target="_blank"
          class="mb-6"
        >
          <v-list-item-content>
            <v-list-item-title class="text-h5 font-weight-medium"
              >CONTACT US</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
      app
      :color="headerColor"
      :dark="!isMobile && dark"
      :flat="true"
      :height="height"
      :class="{
        'mobile-header': isMobile,
        'desktop-header': !isMobile,
      }"
    >
      <router-link class="header-router-link" to="/">
        <img
          :src="settings.logo"
          class="logo-img pa-2 ps-0"
          :style="
            !isMobile && dark
              ? 'filter: brightness(0) invert(1) drop-shadow(0 2px 4px rgba(0,0,0,0.25))'
              : ''
          "
        />
      </router-link>
      <v-spacer />
      <v-sheet
        v-if="!isMobile"
        color="transparent"
        class="pa-0 d-flex align-center"
      >
        <v-btn to="/" text>
          <span
            :class="`text-capitalize ${
              isRTL ? 'mr-2' : 'ml-2'
            } no-letter-spacing`"
            >{{ $t("home") }}</span
          >
        </v-btn>
        <v-btn @click="listingsSearch" text>
          <span
            :class="`text-capitalize ${
              isRTL ? 'mr-2' : 'ml-2'
            } no-letter-spacing`"
            >{{ $t("search") }}</span
          >
        </v-btn>
        <v-btn v-if="settings?.pages?.about?.active" to="/about" text>
          <span
            :class="`text-capitalize ${
              isRTL ? 'mr-2' : 'ml-2'
            } no-letter-spacing`"
            >{{ $t("about_us") }}</span
          >
        </v-btn>
        <v-btn v-if="settings?.pages?.contact?.active" to="/contact" text>
          <span
            :class="`text-capitalize ${
              isRTL ? 'mr-2' : 'ml-2'
            } no-letter-spacing`"
            >{{ $t("contact") }}</span
          >
        </v-btn>
        <v-btn
          v-if="settings?.business?.phone"
          :href="`tel:${settings.business.phone}`"
          text
        >
          <span
            :class="`text-capitalize ${
              isRTL ? 'mr-2' : 'ml-2'
            } no-letter-spacing`"
            >{{ $t("call_us") }}</span
          >
        </v-btn>
        <v-btn
          v-if="settings?.show_whatsapp_link && settings?.business?.phone"
          :href="`https://wa.me/${settings.business.phone.replace(/\D/g, '')}`"
          target="_blank"
          text
        >
          <span
            :class="`text-capitalize ${
              isRTL ? 'mr-2' : 'ml-2'
            } no-letter-spacing`"
            >{{ $t("contact_us") }}</span
          >
        </v-btn>

        <v-btn
          @click="openLangCurrencyModal"
          text
          :class="isRTL ? 'mr-2' : 'ml-2'"
        >
          <v-icon :left="!isRTL" :right="isRTL">mdi-earth</v-icon>
          <span :class="isRTL ? 'mr-2' : 'ml-2'">
            {{
              isRTL
                ? `${currentCurrencyLabel} - ${currentLanguageLabel}`
                : `${currentLanguageLabel} - ${currentCurrencyLabel}`
            }}
          </span>
        </v-btn>
      </v-sheet>
      <v-app-bar-nav-icon
        v-if="isMobile"
        @click.stop="drawer = !drawer"
        :class="`${isRTL ? 'me-2' : 'ms-2'} fixed-nav-icon`"
      />
      <v-btn v-if="isMobile" icon @click="openLangCurrencyModal">
        <v-icon>mdi-earth</v-icon>
      </v-btn>
      <language-currency-picker
        :value="showLangCurrencyModal"
        :languages="settings.languages || []"
        :currencies="settings.currencies || {}"
        :selected-language="selectedLanguage"
        :selected-currency="selectedCurrency"
        @input="showLangCurrencyModal = $event"
        @update:language="onLanguageChange"
        @update:currency="onCurrencyChange"
      />
    </v-app-bar>

    <v-divider
      class="site-header-divider"
      :class="{ 'mobile-divider': isMobile }"
    />
  </div>
</template>

<script>
import { LOGO, LOGO_WHITE } from "@/consts/CMS";
import DeviceMixin from "@/mixins/device-mixin";
import MediaMixin from "@/mixins/media-mixin";
import StylesMixin from "@/mixins/styles-mixin";
import FormattersMixin from "@/mixins/formatters-mixin";
import { mapState } from "vuex";
import LanguageCurrencyPicker from "@/components/common/LanguageCurrencyPicker.vue";

export default {
  name: "site-header",
  components: {
    LanguageCurrencyPicker,
  },
  mixins: [DeviceMixin, MediaMixin, StylesMixin, FormattersMixin],
  computed: {
    ...mapState({
      settings: (state) => state.settingsModule.settings,
    }),

    isAgentSite() {
      return this.settings.site_type === "agent_reserve";
    },
    isPortfolioSite() {
      return this.isAgentSite && !this.$route.query.agent_id;
    },
    portalUrl() {
      return this.settings.portal_url;
    },
    headerColor() {
      if (this.isMobile) {
        return this.dark ? "transparent" : "white";
      }
      return this.dark ? "transparent" : "white";
    },
    currentLanguageLabel() {
      const lang = (this.settings.languages || []).find(
        (l) => l.code === this.selectedLanguage
      );
      return lang ? lang.name : this.selectedLanguage;
    },
    currentCurrencyLabel() {
      return this.selectedCurrency
        ? this.getCurrencySymbol(this.selectedCurrency)
        : "";
    },
    isRTL() {
      return this.$i18n.locale === "he";
    },
  },
  props: {
    dark: {
      type: Boolean,
      default: false,
    },
    flat: {
      type: Boolean,
      default: false,
    },
    inverted: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Boolean,
      default: false,
    },
    height: {
      type: [Number, String],
      default: 64,
    },
  },
  data: () => ({
    LOGO,
    LOGO_WHITE,
    drawer: false,
    showLangCurrencyModal: false,
    selectedLanguage: "",
    selectedCurrency: localStorage.getItem("selectedCurrency") || "",
  }),
  created() {
    // Sync selectedLanguage with current i18n locale
    this.selectedLanguage = this.$i18n.locale;

    // Update localStorage if URL language differs from stored language
    const storedLanguage = localStorage.getItem("selectedLanguage");
    if (storedLanguage !== this.$i18n.locale) {
      localStorage.setItem("selectedLanguage", this.$i18n.locale);
    }
  },
  watch: {
    "$i18n.locale": {
      immediate: true,
      handler(newLocale) {
        if (this.selectedLanguage !== newLocale) {
          this.selectedLanguage = newLocale;
        }
      },
    },
    "settings.languages": {
      immediate: true,
      handler(langs) {
        if (!this.selectedLanguage && langs && langs.length) {
          this.selectedLanguage = langs[0].code;
        }
      },
    },
    "settings.currencies": {
      immediate: true,
      handler(currencies) {
        if (!this.selectedCurrency && currencies && currencies.length) {
          const defaultCurrency =
            currencies.find((c) => c.code === this.settings.currency) ||
            currencies[0];
          this.selectedCurrency = defaultCurrency.code;
        }
      },
    },
  },
  methods: {
    listingsSearch() {
      this.$router.push({
        name: "listings",
        query: { ...this.$route.query, lang: this.$i18n.locale },
      });
      this.drawer = false;
    },
    openLangCurrencyModal() {
      this.showLangCurrencyModal = true;
    },
    onLanguageChange(code) {
      this.selectedLanguage = code;
      localStorage.setItem("selectedLanguage", code);
      this.$i18n.locale = code;

      // Force a page reload to ensure all components update properly
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    onCurrencyChange(code) {
      this.selectedCurrency = code;
      localStorage.setItem("selectedCurrency", code);
      this.$emit("currency-changed", code); // for global listeners
    },
  },
};
</script>

<style lang="scss" scoped>
.mobile-header {
  background-color: white !important;
  transition: all 0.3s ease-in-out;
}

.desktop-header {
  transition: all 0.3s ease-in-out;

  &.v-app-bar--is-scrolled {
    background-color: white !important;
  }
}

.site-header-bar {
  &.v-sheet.v-app-bar.v-toolbar:not(.v-sheet--outlined) {
    box-shadow: 0px 1px 1px 0px map-get($grey, "lighten-3");
  }
}

.v-btn:before {
  opacity: 0 !important;
}

.v-ripple__container {
  opacity: 0 !important;
}

.logo-img {
  object-fit: contain;
  width: 70%;
  height: 100%;
}

.header-router-link {
  height: 100%;
}

.no-letter-spacing {
  letter-spacing: 0 !important;
}

// RTL support for language/currency button
[dir="rtl"] .v-btn .v-icon--right {
  margin-left: 0;
  margin-right: 8px;
}

[dir="rtl"] .v-btn .v-icon--left {
  margin-left: 8px;
  margin-right: 0;
}

.site-header-divider {
  position: relative;
  top: -16px;
}
.mobile-divider {
  top: 4px;
}

.v-badge--overlap .v-badge__badge {
  top: -4px;
  right: -4px;
}
</style>
